import GeoJSON from 'ol/format/GeoJSON';
import { getArea } from 'ol/sphere';
import { intersect } from '@turf/intersect';
import { featureCollection } from '@turf/helpers';

import { getKat } from '../formal';

const geojson = new GeoJSON();
let highlighted;

const plans = ['RbFormålOmråde', 'RpArealformålOmråde', 'KpArealformålOmråde', 'KpArealbrukOmråde'];
const boundingAreas = ['Fremtidig', 'Nåværende', 'RpOmråde', 'KpOmråde'];
export const plansAndTheirBoundingAreas = plans.concat(boundingAreas);

const detectPlankategori = (feature) => {
  switch (feature.get('objtype')?.charAt(0)) {
    case 'F':
      return 'Fremtidig';
      break;
    case 'K':
      return 'Kommuneplan';
      break;
    case 'R':
      return 'Reguleringsplan';
      break;
    default:
      return 'Nåværende';
  }
};

export const getFeatureBy = (key, value, layer) => {
  //  If the callback returns any "truthy" value,
  //  iteration will stop and the function will return the same value
  return layer.getSource().forEachFeature(f => {
    if (f.get(key) === value) return f;
  });
};

export const getPlanIdFormål = (layer, planid) => {
  //  If the callback returns any "truthy" value,
  //  iteration will stop and the function will return the same value
  return layer.getSource().forEachFeature(f => {
    if (f.get('planid') === planid && f.get('formål')) return f.get('formål');
  });
};

export const getHighlightedFeature = () => highlighted;

export const highlightFeature = (f, h) => {
  if (h) {
    f.set('highlight', h);
    highlighted = f;
    // And bring it to top:
  } else {
    f.unset('highlight');
    highlighted = null;
  }
};

export const intersection = (f1, f2) => {
  return geojson.readFeature(
    intersect(featureCollection([
      geojson.writeFeatureObject(f1), geojson.writeFeatureObject(f2)
    ]))
  );
};

export const removeHighlight = () => {
  if (highlighted) highlightFeature(highlighted, false);
};

const r2 = (number) => Math.round(number * 100) / 100; // round to 2 decimals

const getAreaWithoutUnit = (feature) => {
  return feature.getGeometry().getArea(); // matches with ST_Area in tables
  // return getArea(feature.getGeometry(), { projection: 'EPSG:25833' });
};
const roundAndUnit = (a) => {
  return (a > 100000) ? [r2(a / 1000000), 'km²'] : [r2(a), 'm²'];
};
export const getAreaWithUnit = (f) => roundAndUnit(getAreaWithoutUnit(f));

export const enrichInformation = (feature) => {
  const p = feature.getProperties();
  const planid = p.NASJONALAREALPLANID?.planidentifikasjon || p.planid || p.planidentifikasjon || p.arealplanId?.planidentifikasjon || 'ikke angitt';
  feature.set('planid', planid);
  const pt = p.objekttypenavn || p.objtype;
  feature.set('objtype', pt);
  const pk = detectPlankategori(feature);
  feature.set('plankategori', pk);
  const k = p.NASJONALAREALPLANID?.kommunenummer || p.kommunenummer || p.områdeid || p.arealplanId?.kommunenummer;
  if (k) feature.set('kommunenummer', k);
// nibioconfluence.int.nibio.no:8090/display/GEO/Forklaring+arealplaner+SOSI-formatet
  // const pbl = plans.indexOf(pt) > -1;
  const pbl = plansAndTheirBoundingAreas.indexOf(pt) > -1;
  // Areal:
  const [a, u] = getAreaWithUnit(feature);
  if (a > 0) feature.set('arealm2', a);
  feature.set('suitable', pbl && a > 0);
  if (pbl && a > 0) {
    const rb = (pt === 'RbFormålOmråde');
    // const codeYear = rb ? 1985 : 2008;
    const f1985 = p.reguleringsformål;
    const code = rb ? (Array.isArray(f1985) ? f1985[0] : f1985) : (p.arealformål || p.arealbruk);
    if (code) feature.set('formål', code);
    if (code && !p.arealformål) feature.set('arealformål', code);
    const feltnavn = p.feltbetegnelse ? p.feltbetegnelse[0] : '';
    feature.set('feltnavn', feltnavn || '');
    // feature.set('title', feltnavn || planid + '_' + code); // group by feltnanv
    feature.set('title', planid); // group by planid
    if (code) feature.set('kat_nivå1', getKat(code, pk)[0]);
    if (code) feature.set('kat_nivå2', getKat(code, pk)[1]);
    // setId slows down (10sec to 1min - 29MB) uploading large files (ol@7.1)
    // and getFeatureById is not used as of 30/09/2022, I comment it out.
    // feature.setId(feature.ol_uid); // VectorSource#getFeatureById
    feature.set('id', feature.ol_uid);
    feature.set('fremtidigBruk', p.fremtidig || '');
  }
};

export const enrichInformationOnFeatures = (layer) => {
  const sosi = layer.get('sosi');
  layer.getSource().getFeatures().forEach(f => {
    const p = f.getProperties();
    const objtype = p.objekttypenavn || p.objtype;
    if (sosi && plansAndTheirBoundingAreas.indexOf(objtype) < 0) {
      layer.getSource().removeFeature(f);
    } else {
      if (!objtype) { // detect plan type
        const nr = p.identifikasjon?.Identifikasjon?.navnerom;
        const kp = nr.includes('Kommuneplaner');
        // const rp = nr.includes('Reguleringsplaner');
        const f1985 = p.reguleringsformål;
        const af = p.arealformål ? 'Arealformål' : (f1985 ? 'Formål' : '');
        const type = `${kp ? 'K' : 'R'}${f1985 ? 'b' : 'p'}${af}Område`;
        f.set('objtype', type);
        f.set('NASJONALAREALPLANID', p.arealplanId?.NasjonalArealplanId);
      }
      enrichInformation(f);
    }
  });
};

export const keepXY = (geometry) => {
  if (geometry.getFirstCoordinate().length < 3) return;
  const coordinates = geometry.getCoordinates();
  const multi = (geometry.getType().indexOf('Multi') > -1);
  coordinates.forEach(item => { // of a multigeometry
    item.forEach(cc => {
      if (multi) cc.forEach(c => c.splice(2, c.length));
      else cc.splice(2, cc.length);
      // c = c.slice(0, 2); // does not reassign
      // while (c.length > 2) c.pop();
    });
  });
  geometry.setCoordinates(coordinates)
};

